import React from "react"
import { Pencil } from "lucide-react"
import { Link } from "react-router-dom"
import EditActionIconButton from "../common/EditActionIconButton.jsx"
import EditableDropdown from "../common/EditableDropdown.jsx"

const MIN_EDIT_SPINNER_MS = 300

function InfoRow({ label, value, editable = false }) {
    return (
        <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
            <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                {label}:
            </span>
            <div className="d-flex align-items-center">
                <span className="fs-6 fw-semibold text-dark">{value}</span>
                {editable && (
                    <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-primary">
                        <Pencil size={14} />
                    </button>
                )}
            </div>
        </div>
    )
}

function InfoEditableRow({
    label,
    value,
    valueNode = undefined,
    onEditClick = () => {},
    editable = true,
    disabledTooltip = "",
}) {
    return (
        <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
            <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                {label}:
            </span>
            <div className="d-flex align-items-center">
                <span className="fs-6 fw-semibold text-dark">{valueNode ?? value}</span>
                {editable ? (
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-2 text-primary"
                        onClick={onEditClick}
                    >
                        <Pencil size={14} />
                    </button>
                ) : disabledTooltip ? (
                    <span className="d-inline-block" title={disabledTooltip}>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-secondary"
                            disabled
                            aria-disabled="true"
                        >
                            <Pencil size={14} />
                        </button>
                    </span>
                ) : null}
            </div>
        </div>
    )
}

function MasterInfoRow({ label, value, onSave, onError }) {
    const inputRef = React.useRef(/** @type {HTMLInputElement | null} */ (null))
    const resetIconTimerRef = React.useRef(/** @type {number | null} */ (null))
    const [editing, setEditing] = React.useState(false)
    const [saving, setSaving] = React.useState(false)
    const [draftValue, setDraftValue] = React.useState(value ?? "")
    const [iconState, setIconState] = React.useState(
        /** @type {"idle" | "saving" | "success" | "error"} */ ("idle")
    )

    React.useEffect(() => {
        if (!editing) {
            setDraftValue(value ?? "")
        }
    }, [value, editing])

    React.useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editing])

    React.useEffect(() => () => {
        if (resetIconTimerRef.current != null) {
            window.clearTimeout(resetIconTimerRef.current)
        }
    }, [])

    function scheduleIconReset() {
        if (resetIconTimerRef.current != null) {
            window.clearTimeout(resetIconTimerRef.current)
        }
        resetIconTimerRef.current = window.setTimeout(() => {
            setIconState("idle")
            resetIconTimerRef.current = null
        }, 1000)
    }

    async function ensureMinimumSpinnerTime(startedAtMs) {
        const elapsedMs = Date.now() - startedAtMs
        const remainingMs = MIN_EDIT_SPINNER_MS - elapsedMs
        if (remainingMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingMs))
        }
    }

    async function commitChanges() {
        if (saving) {
            return
        }
        const nextValue = draftValue.trim()
        if (!nextValue || nextValue === (value ?? "")) {
            setEditing(false)
            setDraftValue(value ?? "")
            return
        }
        setSaving(true)
        const startedAtMs = Date.now()
        setIconState("saving")
        try {
            await onSave(nextValue)
            await ensureMinimumSpinnerTime(startedAtMs)
            setEditing(false)
            setIconState("success")
            scheduleIconReset()
        } catch (error) {
            await ensureMinimumSpinnerTime(startedAtMs)
            onError(error)
            setDraftValue(value ?? "")
            setEditing(false)
            setIconState("error")
            scheduleIconReset()
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
            <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                {label}:
            </span>
            <div className="d-flex align-items-center">
                <input
                    ref={inputRef}
                    type="text"
                    value={draftValue}
                    readOnly={!editing || saving}
                    className="form-control-plaintext fs-6 fw-semibold text-dark px-2 py-0 border-bottom"
                    style={{
                        minWidth: "7rem",
                        maxWidth: "11rem",
                        backgroundColor: editing ? "#faf9d9" : "transparent",
                    }}
                    onChange={(event) => setDraftValue(event.currentTarget.value)}
                    onBlur={() => {
                        if (editing) {
                            void commitChanges()
                        }
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault()
                            void commitChanges()
                        } else if (event.key === "Escape") {
                            setDraftValue(value ?? "")
                            setEditing(false)
                        }
                    }}
                />
                <div onMouseDown={(event) => event.preventDefault()}>
                    <EditActionIconButton
                        state={iconState}
                        disabled={saving}
                        onClick={() => {
                            if (editing) {
                                void commitChanges()
                            } else {
                                setEditing(true)
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

function getDefaultFieldLayout() {
    return {
        left: [
            { kind: "master", label: "Master" },
            { kind: "manifest", label: "Manifest" },
            { kind: "lodge", label: "Lodge" },
            { kind: "text", key: "depot", label: "Depot" },
            { kind: "pickUpAt", label: "Pick Up At" },
            { kind: "text", key: "orderDate", label: "Order Date" },
            { kind: "text", key: "staffInitial", label: "Initial" },
            { kind: "text", key: "confirmationEmail", label: "Email" },
            { kind: "text", key: "preferredContactMethod", label: "Contact" },
        ],
        right: [
            { kind: "text", key: "siNumber", label: "SJ" },
            { kind: "text", key: "salesOrder", label: "SO" },
            { kind: "text", key: "shipment", label: "Shipment" },
            { kind: "text", key: "invoice", label: "Invoice" },
            { kind: "checkbox", key: "isStaff", label: "Is Staff" },
            { kind: "frozenShipDate", key: "frozenShipDate", label: "Frozen Ship Date" },
        ],
    }
}

function OrderInfoField({ field, orderDetails, onMasterSave, onMasterSaveError, onLodgeSave, onLodgeSaveError, lodgeOptions, onPickUpAtSave, onPickUpAtSaveError, pickUpAtOptions, pickUpAtDisabled, onEditManifestClick, enableManifestEdit }) {
    if (field?.kind === "master") {
        if (field?.editable === false) {
            return <InfoRow label={field.label ?? "Master"} value={orderDetails.master} />
        }
        return (
            <MasterInfoRow
                label={field.label ?? "Master"}
                value={orderDetails.master}
                onSave={onMasterSave}
                onError={onMasterSaveError}
            />
        )
    }

    if (field?.kind === "manifest") {
        const to = typeof field?.linkTo === "function" ? field.linkTo(orderDetails) : null
        const valueNode = to ? (
            <Link to={to} className="link-primary text-decoration-underline">
                {orderDetails.manifest}
            </Link>
        ) : undefined
        return (
            <InfoEditableRow
                label={field.label ?? "Manifest"}
                value={orderDetails.manifest}
                valueNode={valueNode}
                onEditClick={onEditManifestClick}
                editable={enableManifestEdit}
                disabledTooltip={enableManifestEdit ? "" : "Cannot edit manifests while offline."}
            />
        )
    }

    if (field?.kind === "lodge") {
        return (
            <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
                <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                    {(field.label ?? "Lodge")}:
                </span>
                <div className="flex-grow-1 overflow-hidden">
                    <EditableDropdown
                        value={orderDetails.lodgeId}
                        displayValue={orderDetails.lodge}
                        options={lodgeOptions}
                        onSave={onLodgeSave}
                        onError={onLodgeSaveError}
                        width="11rem"
                        showRevertButton={false}
                    />
                </div>
            </div>
        )
    }

    if (field?.kind === "pickUpAt") {
        if (!orderDetails?.isPickup) {
            return null
        }

        return (
            <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
                <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                    {(field.label ?? "Pick Up At")}:
                </span>
                <div className="flex-grow-1 overflow-hidden">
                    <EditableDropdown
                        value={orderDetails.pickUpAtId}
                        displayValue={orderDetails.pickUpAt}
                        options={pickUpAtOptions}
                        onSave={onPickUpAtSave}
                        onError={onPickUpAtSaveError}
                        disabled={pickUpAtDisabled}
                        width="11rem"
                        showRevertButton={false}
                    />
                </div>
            </div>
        )
    }

    if (field?.kind === "checkbox") {
        const checked = Boolean(field.key ? orderDetails?.[field.key] : false)
        return (
            <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
                <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                    {(field.label ?? "—")}:
                </span>
                <input type="checkbox" checked={checked} readOnly className="form-check-input" />
            </div>
        )
    }

    if (field?.kind === "frozenShipDate") {
        const value = field.key ? (orderDetails?.[field.key] ?? "—") : "—"
        return (
            <div className="d-flex align-items-center py-2" style={{ minHeight: "2rem" }}>
                <span className="fs-6 text-secondary flex-shrink-0" style={{ width: "6rem" }}>
                    {(field.label ?? "Frozen Ship Date")}:
                </span>
                <input
                    type="text"
                    value={value}
                    readOnly
                    className="form-control-plaintext fs-6 fw-semibold text-dark p-0"
                />
            </div>
        )
    }

    const label = field?.label ?? "—"
    const value = field?.key ? (orderDetails?.[field.key] ?? "—") : "—"
    return <InfoRow label={label} value={value} />
}

export default function OrderInformation({
    orderDetails,
    fieldLayout = undefined,
    onMasterSave = async (_master) => {},
    onMasterSaveError = (_error) => {},
    onLodgeSave = async (_lodgeValue) => {},
    onLodgeSaveError = (_error) => {},
    lodgeOptions = /** @type {{ value: string, label: string }[]} */ ([]),
    onPickUpAtSave = async (_pickUpAtValue) => {},
    onPickUpAtSaveError = (_error) => {},
    pickUpAtOptions = /** @type {{ value: string, label: string }[]} */ ([]),
    pickUpAtDisabled = false,
    onEditManifestClick = () => {},
    enableManifestEdit = true,
}) {
    const layout = fieldLayout ?? getDefaultFieldLayout()
    return (
        <div className="rounded p-4 h-100">
            <div className="row gx-5 gy-4">
                <div className="col-12 col-xl-6">
                    <div className="d-flex flex-column gap-1">
                        {(layout.left ?? []).map((field, idx) => (
                            <OrderInfoField
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${field?.kind ?? "text"}-${field?.key ?? field?.label ?? "field"}-${idx}`}
                                field={field}
                                orderDetails={orderDetails}
                                onMasterSave={onMasterSave}
                                onMasterSaveError={onMasterSaveError}
                                onLodgeSave={onLodgeSave}
                                onLodgeSaveError={onLodgeSaveError}
                                lodgeOptions={lodgeOptions}
                                onPickUpAtSave={onPickUpAtSave}
                                onPickUpAtSaveError={onPickUpAtSaveError}
                                pickUpAtOptions={pickUpAtOptions}
                                pickUpAtDisabled={pickUpAtDisabled}
                                onEditManifestClick={onEditManifestClick}
                                enableManifestEdit={enableManifestEdit}
                            />
                        ))}
                    </div>
                </div>
                <div className="col-12 col-xl-6">
                    <div className="d-flex flex-column gap-1">
                        {(layout.right ?? []).map((field, idx) => (
                            <OrderInfoField
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${field?.kind ?? "text"}-${field?.key ?? field?.label ?? "field"}-${idx}`}
                                field={field}
                                orderDetails={orderDetails}
                                onMasterSave={onMasterSave}
                                onMasterSaveError={onMasterSaveError}
                                onLodgeSave={onLodgeSave}
                                onLodgeSaveError={onLodgeSaveError}
                                lodgeOptions={lodgeOptions}
                                onPickUpAtSave={onPickUpAtSave}
                                onPickUpAtSaveError={onPickUpAtSaveError}
                                pickUpAtOptions={pickUpAtOptions}
                                pickUpAtDisabled={pickUpAtDisabled}
                                onEditManifestClick={onEditManifestClick}
                                enableManifestEdit={enableManifestEdit}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
